import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Coins, Plus, Minus, Trash, ArrowCounterClockwise, Building, Star, Receipt, UserPlus, Upload, ChatCircleText, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { CustomChargeModal } from "@/components/CustomChargeModal";
import { CreateClientModal } from "@/components/CreateClientModal";
import { BulkImportClientsModal } from "@/components/BulkImportClientsModal";
import { EditClientModal } from "@/components/EditClientModal";

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [editing, setEditing] = useState(null);
  const [delta, setDelta] = useState("");
  const [note, setNote] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteMode, setDeleteMode] = useState("soft");
  const [chargeTarget, setChargeTarget] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editingInfo, setEditingInfo] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = async () => {
    try {
      const { data } = await api.get("/admin/clients");
      setClients(data);
    } catch (_) {}
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setCreateOpen(true);
      // Clean the URL so reloading doesn't re-open the modal
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const adjust = async (sign) => {
    if (!editing || !delta) return;
    try {
      await api.post("/admin/points/adjust", {
        user_id: editing.id,
        delta: sign * parseFloat(delta),
        note,
      });
      toast.success("Balance updated");
      setEditing(null);
      setDelta("");
      setNote("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const reactivate = async (c) => {
    try {
      await api.post(`/admin/clients/${c.id}/reactivate`);
      toast.success("Reactivated");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const requestReview = async (c) => {
    try {
      await api.post("/admin/reviews/request", { client_id: c.id });
      toast.success(`Review request emailed to ${c.email}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send");
    }
  };

  const toggleMaintenance = async (c) => {
    try {
      await api.patch(`/admin/clients/${c.id}/maintenance`, {
        has_maintenance_plan: !c.has_maintenance_plan,
      });
      toast.success(c.has_maintenance_plan ? "Maintenance plan removed" : "Maintenance plan activated");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const performDelete = async () => {
    if (!deleteTarget) return;
    const isHard = deleteMode === "hard";
    try {
      await api.delete(`/admin/clients/${deleteTarget.id}${isHard ? "?hard=true" : ""}`);
      toast.success(isHard ? "Client and all data permanently deleted" : "Client deactivated");
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <AdminLayout>
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">Clients</div>
          <h1 className="font-display text-5xl mt-2">All clients</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setBulkOpen(true)}
            data-testid="bulk-import-btn"
            className="btn-outline px-4 py-3 text-sm uppercase tracking-wider flex items-center gap-2"
          >
            <Upload size={14} /> Import CSV
          </button>
          <button
            onClick={() => setCreateOpen(true)}
            data-testid="add-client-btn"
            className="btn-primary px-5 py-3 text-sm font-bold uppercase tracking-wider flex items-center gap-2"
          >
            <UserPlus size={14} weight="bold" /> Add client
          </button>
        </div>
      </div>

      <div className="border border-[#262626] divide-y divide-[#262626]" data-testid="admin-clients-list">
        <div className="hidden md:grid grid-cols-12 gap-4 p-4 bg-[#0f0f0f] text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3]">
          <div className="col-span-3">Name</div>
          <div className="col-span-3">Company</div>
          <div className="col-span-3">Email</div>
          <div className="col-span-1">Projects</div>
          <div className="col-span-1">Points</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {clients.map((c) => (
          <div
            key={c.id}
            className={`grid grid-cols-12 gap-4 p-4 items-center ${c.active === false ? "bg-[#0a0a0a]/60 opacity-60" : "bg-[#0a0a0a]"}`}
            data-testid={`client-row-${c.id}`}
          >
            <div className="col-span-12 md:col-span-3 font-semibold flex items-center gap-2 flex-wrap">
              {c.name}
              {c.active === false && (
                <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-[#404040] text-[#a3a3a3]">Deactivated</span>
              )}
              {c.has_maintenance_plan && (
                <span data-testid={`plan-badge-${c.id}`} className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-[#FF2D2D] bg-[#FF2D2D]/10 text-[#FF2D2D] flex items-center gap-1">
                  <Star size={9} weight="fill" /> Plan
                </span>
              )}
            </div>
            <div className="col-span-6 md:col-span-3 text-sm text-[#a3a3a3] flex items-center gap-1.5">
              {c.company_name ? <><Building size={12} className="shrink-0" /> {c.company_name}</> : <span className="text-[#444]">—</span>}
            </div>
            <div className="col-span-6 md:col-span-3 text-sm text-[#a3a3a3] truncate">{c.email}</div>
            <div className="col-span-3 md:col-span-1 text-sm">{c.project_count}</div>
            <div className="col-span-3 md:col-span-1 text-sm flex items-center gap-1">
              <Coins size={14} weight="fill" className="text-[#FF2D2D]" />
              <span className="font-semibold">{(c.points_balance ?? 0).toFixed(2)}</span>
            </div>
            <div className="col-span-6 md:col-span-1 flex justify-end gap-1.5 flex-wrap">
              <button
                onClick={() => setChargeTarget(c)}
                data-testid={`send-charge-${c.id}`}
                title="Send a custom charge"
                className="px-2 py-1 text-xs border border-[#262626] text-[#a3a3a3] hover:text-[#FF2D2D] hover:border-[#FF2D2D] flex items-center gap-1"
              >
                <Receipt size={11} />
                <span className="hidden sm:inline">Charge</span>
              </button>
              <button
                onClick={() => requestReview(c)}
                data-testid={`request-review-${c.id}`}
                title="Email this client a review request"
                className="px-2 py-1 text-xs border border-[#262626] text-[#a3a3a3] hover:text-[#FF2D2D] hover:border-[#FF2D2D] flex items-center gap-1"
              >
                <ChatCircleText size={11} />
                <span className="hidden sm:inline">Review</span>
              </button>
              <button
                onClick={() => toggleMaintenance(c)}
                data-testid={`toggle-maintenance-${c.id}`}
                title={c.has_maintenance_plan ? "Remove maintenance plan" : "Activate maintenance plan"}
                className={`px-2 py-1 text-xs border ${c.has_maintenance_plan ? "border-[#FF2D2D] bg-[#FF2D2D]/10 text-[#FF2D2D]" : "border-[#262626] text-[#a3a3a3] hover:text-[#FF2D2D] hover:border-[#FF2D2D]"}`}
              >
                <Star size={11} weight={c.has_maintenance_plan ? "fill" : "regular"} />
              </button>
              <button
                onClick={() => setEditingInfo(c)}
                data-testid={`edit-info-${c.id}`}
                title="Edit client info"
                className="px-2 py-1 text-xs border border-[#262626] text-[#a3a3a3] hover:text-[#FF2D2D] hover:border-[#FF2D2D] flex items-center gap-1"
              >
                <PencilSimple size={11} />
                <span className="hidden sm:inline">Edit</span>
              </button>
              <button
                onClick={() => setEditing(c)}
                data-testid={`adjust-points-${c.id}`}
                className="btn-outline px-2 py-1 text-xs"
                title="Adjust points"
              >
                Adjust
              </button>
              {c.active === false ? (
                <button
                  onClick={() => reactivate(c)}
                  data-testid={`reactivate-${c.id}`}
                  className="px-2 py-1 text-xs border border-[#262626] text-[#a3a3a3] hover:text-[#FF2D2D] hover:border-[#FF2D2D]"
                  title="Reactivate"
                >
                  <ArrowCounterClockwise size={12} />
                </button>
              ) : null}
              <button
                onClick={() => { setDeleteTarget(c); setDeleteMode("soft"); }}
                data-testid={`delete-client-${c.id}`}
                className="px-2 py-1 text-xs border border-[#262626] text-[#a3a3a3] hover:text-[#FF2D2D] hover:border-[#FF2D2D]"
                title="Delete client"
              >
                <Trash size={12} />
              </button>
            </div>
          </div>
        ))}
        {clients.length === 0 && <div className="bg-[#0a0a0a] p-8 text-center text-[#a3a3a3]">No clients yet.</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="adjust-modal">
          <div className="border border-[#262626] bg-[#0a0a0a] w-full max-w-md p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">Adjust points</div>
            <h3 className="font-display text-2xl mt-2">{editing.name}</h3>
            <p className="text-sm text-[#a3a3a3]">Current: {(editing.points_balance ?? 0).toFixed(2)} pts</p>

            <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mt-5 mb-2">Amount</label>
            <input
              type="number"
              step="0.25"
              min="0"
              value={delta}
              onChange={(e) => setDelta(e.target.value)}
              data-testid="adjust-delta"
              className="w-full bg-[#050505] border border-[#262626] px-4 py-3 focus:border-[#FF2D2D] outline-none"
            />

            <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mt-4 mb-2">Note</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="adjust-note"
              className="w-full bg-[#050505] border border-[#262626] px-4 py-3 focus:border-[#FF2D2D] outline-none"
            />

            <div className="flex gap-3 mt-6">
              <button onClick={() => adjust(1)} data-testid="adjust-add" className="btn-primary flex-1 px-4 py-3 text-sm font-bold uppercase tracking-wider flex items-center justify-center gap-2">
                <Plus size={14} weight="bold" /> Add
              </button>
              <button onClick={() => adjust(-1)} data-testid="adjust-subtract" className="btn-outline flex-1 px-4 py-3 text-sm uppercase tracking-wider flex items-center justify-center gap-2">
                <Minus size={14} /> Subtract
              </button>
            </div>
            <button onClick={() => setEditing(null)} data-testid="adjust-cancel" className="mt-3 w-full text-xs text-[#a3a3a3] hover:text-white">Cancel</button>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="delete-modal">
          <div className="border border-[#FF2D2D] bg-[#0a0a0a] w-full max-w-md p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">Delete client</div>
            <h3 className="font-display text-2xl mt-2">{deleteTarget.name}</h3>
            <p className="text-sm text-[#a3a3a3] mt-1">{deleteTarget.email}</p>

            <div className="mt-5 space-y-3">
              <label className={`flex items-start gap-3 p-3 border cursor-pointer ${deleteMode === "soft" ? "border-[#FF2D2D] bg-[#FF2D2D]/5" : "border-[#262626]"}`}>
                <input
                  type="radio"
                  name="delMode"
                  value="soft"
                  checked={deleteMode === "soft"}
                  onChange={() => setDeleteMode("soft")}
                  data-testid="delete-mode-soft"
                  className="mt-1"
                />
                <div>
                  <div className="font-semibold text-sm">Deactivate (recommended)</div>
                  <div className="text-xs text-[#a3a3a3] mt-1">Login blocked. Projects, messages, and payment history are retained for records. Reversible.</div>
                </div>
              </label>
              <label className={`flex items-start gap-3 p-3 border cursor-pointer ${deleteMode === "hard" ? "border-[#FF2D2D] bg-[#FF2D2D]/5" : "border-[#262626]"}`}>
                <input
                  type="radio"
                  name="delMode"
                  value="hard"
                  checked={deleteMode === "hard"}
                  onChange={() => setDeleteMode("hard")}
                  data-testid="delete-mode-hard"
                  className="mt-1"
                />
                <div>
                  <div className="font-semibold text-sm">Permanently delete</div>
                  <div className="text-xs text-[#a3a3a3] mt-1">Removes the user, all their projects ({deleteTarget.project_count}), deliverables, messages, surveys, and payment records. <strong className="text-[#FF2D2D]">Cannot be undone.</strong></div>
                </div>
              </label>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={performDelete} data-testid="delete-confirm" className="btn-primary flex-1 px-4 py-3 text-sm font-bold uppercase tracking-wider">
                {deleteMode === "hard" ? "Permanently delete" : "Deactivate"}
              </button>
              <button onClick={() => setDeleteTarget(null)} data-testid="delete-cancel" className="btn-outline flex-1 px-4 py-3 text-sm uppercase tracking-wider">Cancel</button>
            </div>
          </div>
        </div>
      )}

      <CustomChargeModal
        open={!!chargeTarget}
        client={chargeTarget}
        onClose={() => setChargeTarget(null)}
      />
      <CreateClientModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => load()}
      />
      <BulkImportClientsModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onCompleted={() => load()}
      />
      <EditClientModal
        open={!!editingInfo}
        client={editingInfo}
        onClose={() => setEditingInfo(null)}
        onSaved={() => load()}
      />
    </AdminLayout>
  );
}
